import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import ChatIcon from "@mui/icons-material/Chat"
import FavoriteIcon from "@mui/icons-material/Favorite"
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder"
import {
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  IconButton,
  Typography,
} from "@mui/material"
import { makeStyles } from "tss-react/mui-compat"
import AspectRatio from "components/common/AspectRatio"
import { FallbackImage } from "components/common/FallbackImage"
import Link, { LinkProps } from "next/link"
import React, { useState } from "react"

interface ExhibitorCardProps {
  isFavorite: boolean
  isChatEnabled: boolean

  onFavorite: () => void
  onChat: () => void

  headerText: string
  subheaderText: string

  imageSrc?: string | null
  imageAlt?: string
  href: LinkProps["href"]
}

const useStyles = makeStyles()(
  (theme) => ({
    card: {
      display: "flex",
      flex: 1,
      flexFlow: "nowrap column",

      minWidth: 280,
      minHeight: 297,

      borderRadius: 10,

      boxShadow: "none",
      border: `1px solid ${theme.palette.gray.main}`,
    },
    cardActionArea: {
      position: "relative",
      display: "flex",
      flex: 1,
      flexFlow: "nowrap column",
    },
    imageContainer: {
      width: "100%",
      order: 0,
      position: "relative",
    },
    contentLink: {
      display: "block",
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    contentContainer: {
      flex: 1,
      order: 1,
      display: "flex",
      flexFlow: "column nowrap",
      width: "100%",
    },
    cardContent: {
      display: "flex",
      flexFlow: "column nowrap",
      padding: "15px 10px 0 10px",
    },
    subheaderTypography: {
      order: 0,

      ...theme.fonts.lato400,
      fontSize: 14,
      lineHeight: "16.8px",
    },
    headerTypography: {
      order: 1,

      ...theme.fonts.raleway600,
      fontSize: 24,
      lineHeight: "24px",

      marginTop: 10,
      marginBottom: 0,
    },
    cardActions: {
      order: 2,
      padding: "0 2px 2px 2px",

      display: "flex",
      flexFlow: "row nowrap",
    },
    iconButton: {
      color: "#000000",
      fontSize: 24,
      padding: 10,

      "& .MuiTouchRipple-child": {
        backgroundColor: theme.palette.primary.light,
      },
      "&.MuiButtonBase-root": {
        margin: 0,
        "&:hover": {
          backgroundColor: theme.palette.primary.light,
        },
      },
    },
    arrowForwardIconButton: {
      "&.MuiButtonBase-root": {
        marginLeft: "auto",
      },
    },
  }),
)

const ExhibitorCard = (props: ExhibitorCardProps) => {
  const {
    isFavorite,

    onFavorite,
    onChat,

    href,

    subheaderText,
    headerText,

    imageSrc,
    imageAlt,
    isChatEnabled,
  } = props
  const { classes, cx } = useStyles()
  const [shouldShowFallbackImage, setShouldShowFallbackImage] = useState(false)

  const onCardMediaError = () => {
    if (shouldShowFallbackImage) return

    console.error(`${headerText} exhibitor has failed to load the cover image thumbnail`, imageSrc)
    setShouldShowFallbackImage(true)
  }

  return (
    <Card className={classes.card}>
      <CardActionArea className={classes.cardActionArea}>
        <Box className={classes.contentContainer}>
          <CardContent className={classes.cardContent}>
            <Typography variant="h2" className={classes.headerTypography}>
              {headerText}
            </Typography>
            <Typography variant="overline" component="p" className={classes.subheaderTypography}>
              {subheaderText}
            </Typography>
          </CardContent>
        </Box>
        <AspectRatio ratio={140 / 79} className={classes.imageContainer}>
          {shouldShowFallbackImage || !imageSrc ? (
            <FallbackImage ariaLabel="Exhibitor default image" />
          ) : (
            <CardMedia
              onError={onCardMediaError}
              component="img"
              image={imageSrc}
              alt={imageAlt}
              aria-hidden={!imageAlt}
            />
          )}
        </AspectRatio>
        <Link href={href} passHref legacyBehavior>
          <a className={classes.contentLink} aria-label={`Open ${headerText}`} />
        </Link>
      </CardActionArea>
      <CardActions className={classes.cardActions}>
        <IconButton
          aria-label={isFavorite ? "favorite" : "unfavorite"}
          onClick={onFavorite}
          className={classes.iconButton}
          size="large">
          {isFavorite ? (
            <FavoriteIcon color="inherit" fontSize="inherit" />
          ) : (
            <FavoriteBorderIcon color="inherit" fontSize="inherit" />
          )}
        </IconButton>
        {isChatEnabled && (
          <IconButton
            aria-label="open chat"
            onClick={onChat}
            className={classes.iconButton}
            size="large">
            <ChatIcon color="inherit" fontSize="inherit" />
          </IconButton>
        )}
        <Link href={href} passHref legacyBehavior>
          <IconButton
            aria-label={`open ${headerText}`}
            component="a"
            className={cx(classes.iconButton, classes.arrowForwardIconButton)}
            size="large">
            <ArrowForwardIcon color="inherit" fontSize="inherit" />
          </IconButton>
        </Link>
      </CardActions>
    </Card>
  )
}

export default ExhibitorCard
